import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePopupFlow } from '../../hooks/usePopupFlow';
import { startAuthentication } from '@simplewebauthn/browser';
import api from '../../api/axios';
import {
  UserIcon,
  LockIcon,
  EyeIcon,
  EyeOffIcon,
  AlertCircleIcon,
  CheckIcon,
  ArrowRightIcon
} from '../../components/Icons';

export default function LoginForm() {
  const [identity, setIdentity] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [biometricErrorMsg, setBiometricErrorMsg] = useState('');

  const auth = useAuth();
  const { handleLoginSuccess } = usePopupFlow();

  const handleBiometricLogin = async () => {
    setBiometricErrorMsg('');
    setSubmitting(true);
    try {
      // 1. Fetch options
      const res = await api.post('/auth/biometric/login/options', {}, { withCredentials: true });
      const options = res.data;

      // 2. Prompt user
      const assertion = await startAuthentication(options);

      // 3. Send response to backend
      const verifyRes = await api.post('/auth/biometric/login', assertion, { withCredentials: true });
      
      // Complete login in AuthContext
      const result = await auth.completeBiometricLogin(verifyRes.data);
      if (result && result.accessToken) {
        if (typeof window !== 'undefined' && window.opener) {
          await handleLoginSuccess(result.accessToken);
          return;
        }
        window.location.href = '/u/0/dashboard/overview';
      }
    } catch (err) {
      console.error('Biometric login failed', err);
      setBiometricErrorMsg(err?.response?.data?.message || 'Biometric authentication failed or cancelled.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!identity.trim()) {
      setErrorMsg('Please enter your email, phone, or username');
      return;
    }
    if (!password) {
      setErrorMsg('Please enter your password');
      return;
    }
    try {
      setSubmitting(true);
      const result = await auth.login(identity.trim(), password, rememberMe);
      if (result && result.accessToken) {
        if (typeof window !== 'undefined' && window.opener) {
          await handleLoginSuccess(result.accessToken);
          return;
        }
        window.location.href = '/u/0/dashboard/overview';
      }
    } catch (err) {
      console.error('Login failed', err);
      setErrorMsg(err?.response?.data?.message || 'Invalid login credentials. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {errorMsg && (
        <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-600 dark:text-rose-400 text-xs flex items-start gap-2">
          <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Identity Input */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1" htmlFor="identity">
          Email, Phone, or Username
        </label>
        <div className="relative">
          <UserIcon className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <input
            id="identity"
            name="identity"
            type="text"
            value={identity}
            onChange={(e) => setIdentity(e.target.value)}
            placeholder="e.g. john@company.com or +14155552671"
            className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            autoComplete="username"
            disabled={submitting}
          />
        </div>
      </div>

      {/* Password Input */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300" htmlFor="password">
            Password
          </label>
        </div>
        <div className="relative">
          <LockIcon className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••••••"
            className="w-full pl-9 pr-10 py-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            autoComplete="current-password"
            disabled={submitting}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >
            {showPassword ? <EyeOffIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Remember Me */}
      <div className="flex items-center justify-between text-xs">
        <label className="flex items-center gap-2 text-slate-600 dark:text-slate-400 cursor-pointer">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 dark:border-slate-700 text-indigo-600 focus:ring-indigo-500"
          />
          <span>Remember me for 30 days</span>
        </label>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={submitting}
        className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 text-white font-semibold text-xs rounded-xl shadow-md shadow-indigo-600/20 transition-all flex items-center justify-center gap-2"
      >
        <span>{submitting ? 'Authenticating...' : 'Sign In'}</span>
        {!submitting && <ArrowRightIcon className="w-4 h-4" />}
      </button>

      <div className="relative flex py-2 items-center">
        <div className="flex-grow border-t border-slate-200 dark:border-slate-700"></div>
        <span className="flex-shrink-0 mx-4 text-slate-400 text-xs">OR</span>
        <div className="flex-grow border-t border-slate-200 dark:border-slate-700"></div>
      </div>

      {biometricErrorMsg && (
        <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-600 dark:text-rose-400 text-xs flex items-start gap-2 mb-2">
          <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{biometricErrorMsg}</span>
        </div>
      )}

      <button
        type="button"
        onClick={handleBiometricLogin}
        disabled={submitting}
        className="w-full py-2.5 px-4 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-semibold text-xs rounded-xl transition-all flex items-center justify-center gap-2"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
        </svg>
        <span>Sign in with Passkey / Biometrics</span>
      </button>
    </form>
  );
}
